import { memoize } from 'lodash';
import { RESOURCE_TYPES } from 'util/project/helpers';


// `lodash.memoize` takes the first parameter to create the cache key
const filterByType = memoize((targetType, resources) => resources.filter(({ type }) => type === targetType));
const findById = memoize((targetId, resources) => resources.find(({ id }) => id === targetId));
const findBySlug = memoize(({ targetSlug, targetType }, resources) => {
    const haystack = targetType ? filterByType(targetType, resources) : resources;
    return haystack.find(({ slug }) => slug === targetSlug);
});

/**
 * Get the active root category
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @return {ICategory} The active main category
 */
export const activeRootCategory = (state, getters) => {
    // There's always only one root category, currently
    return getters.rootCategories[0];
};

/**
 * Get the active main category
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @param {any} rootState - The vuex store's state
 * @return {ICategory} The active main category
 */
export const activeMainCategory = (state, getters, rootState) => {
    return getters.getResourceBySlug(rootState.route.params.maincategory, RESOURCE_TYPES.MAIN);
};

/**
 * Get the active entry
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @param {any} rootState - The vuex store's state
 * @return {IEntry} The active entry
 */
export const activeEntry = (state, getters, rootState) => {
    return getters.getResourceBySlug(rootState.route.params.entry, RESOURCE_TYPES.ENTRY);
};

/**
 * Get all root categories
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @return {ICategory[]} All root categories
 */
export const rootCategories = (state, getters) => getters.getResourcesByType(RESOURCE_TYPES.ROOT);

/**
 * Get all main categories
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @return {ICategory[]} All main categories
 */
export const mainCategories = (state, getters) => getters.getResourcesByType(RESOURCE_TYPES.MAIN);

/**
 * Get all sub categories
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @return {ICategory[]} All main categories
 */
export const subCategories = (state, getters) => getters.getResourcesByType(RESOURCE_TYPES.SUB);

/**
 * Get all entries
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @return {IEntry[]} All entries
 */
export const entries = (state, getters) => getters.getResourcesByType(RESOURCE_TYPES.ENTRY);

/**
 * Get entries filtered by a category slug
 * @param {IProjectDataState} state - The vuex module's state
 * @param {any} getters - A list of the module's getters
 * @return {IEntry[]} The list of entries
 */
export const getEntriesByCategorySlug = (state, getters) => targetSlug => {
    const targetCategory = getters.getResourceBySlug(targetSlug) || [];
    return targetCategory.entryIds.map(entryId => getters.getResourceById(entryId));
};

/**
 * Get a resource by a resource id
 * @param {IResource[]} resources - The list of resources
 * @return {IResource} The target resource
 */
export const getResourceById = ({ resources }) => targetId => findById(targetId, resources);

/**
 * Get a resource from a list of resources using a slug
 * @param {IResource[]} resources - The list of resources
 * @param {any} getters - A list of the module's getters
 * @return {IResource} The search result
 */
export const getResourceBySlug = ({ resources }) => (targetSlug, targetType) => {
    return findBySlug({ targetSlug, targetType }, resources);
};

/**
 * Get all categories from a list of categories using a type
 * @param {IResource[]} resources - The list of resources
 * @param {number} targetType - The category type
 * @return {IResource[]}
 */
export const getResourcesByType = ({ resources }) => targetType => filterByType(targetType, resources);
